import React, { forwardRef, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row } from 'reactstrap';
import classnames from 'classnames';
import ReactPlayer from 'react-player';
import SanitizedReactHtmlParser from '../../../../../../../utils/helpers/SanitizedReactHtmlParser';
import telegramIcon from './telegram-black.svg';

const TelegramBody = forwardRef(({ telegramContent, title }, ref) => {
  const { type, id } = JSON.parse(telegramContent);

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const child = document.createElement('script');
    child.async = true;
    child.src = 'https://telegram.org/js/telegram-widget.js?15';
    child.dataset.width = '100%';

    if (type === 'post') {
      child.dataset.telegramPost = id;
    }

    if (type === 'discussion') {
      child.dataset.telegramDiscussion = id;
    }

    ref.current.append(child);
  }, [ref]);

  return (
    <>
      <span className="font-20 font-weight-semi">
        <img src={telegramIcon} alt="Telegram Icon" width="20px" className="mr-3" />
        <span>{title}</span>
      </span>
      <Row className="flex-wrap-reverse">
        <Col
          className={classnames('mt-lg-4 mt-3')}
          lg={12}
        >
          <div ref={ref} />
        </Col>
      </Row>
    </>
  );
});

TelegramBody.propTypes = {
  telegramContent: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

const TextBody = ({ title, content }) => (
  <>
    <span className="font-20 font-weight-semi">
      <i className={classnames('mr-3 content-card__title-icon icon-account')} />
      <span>{title}</span>
    </span>
    <Row className="flex-wrap-reverse">
      <Col
        className={classnames('mt-lg-4 mt-3')}
        lg={12}
      >
        <span className="subtext font-16">
          {SanitizedReactHtmlParser(content)}
        </span>
      </Col>
    </Row>
  </>
);

TextBody.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
};

const VideoBody = ({ resource, content, title }) => (
  <>
    <span className="font-20 font-weight-semi">
      <i className={classnames('mr-3 content-card__title-icon icon-video')} />
      <span>{title}</span>
    </span>
    <Row className="flex-wrap-reverse">
      <Col
        className={classnames('mt-lg-3 mt-2')}
        lg={12}
      >
        <ReactPlayer url={resource || ''} height="320px" width="100%" controls />
      </Col>
      <Col
        className={classnames('mt-lg-4 mt-3')}
        lg={12}
      >
        <span className="subtext font-16">
          {SanitizedReactHtmlParser(content)}
        </span>
      </Col>
    </Row>
  </>
);

VideoBody.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  resource: PropTypes.string.isRequired,
};

const ImageBody = ({ resource, alignment, content }) => (
  <Row className="flex-wrap">
    <Col
      className={classnames('mt-lg-4 mt-3', { 'order-1': alignment === 'right' })}
      lg={12}
    >
      <img alt="content" src={resource} width="100%" />
    </Col>
    <Col
      className={classnames('mt-lg-3 mt-2')}
      lg={12}
    >
      <span className="subtext font-16">
        {SanitizedReactHtmlParser(content)}
      </span>
    </Col>
  </Row>
);

ImageBody.propTypes = {
  alignment: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  resource: PropTypes.string.isRequired,
};

const ContentCard = ({
  content: {
    content, attributeType, resource, alignment, title,
  },
}) => {
  const isImg = attributeType === 'img_with_description';
  const isVideo = attributeType === 'video';
  const isTelegram = attributeType === 'telegram';
  const isText = attributeType === 'text';

  const telegramRef = useRef(null);

  return (
    <Card className="border-0 shadow-none mb-2 mb-lg-3">
      { isText && <TextBody content={content} title={title} /> }
      { isVideo && <VideoBody resource={resource || ''} content={content} title={title} /> }
      { isImg && <ImageBody resource={resource || ''} alignment={alignment} content={content} /> }
      { isTelegram && <TelegramBody telegramContent={content} title={title} ref={telegramRef} /> }
    </Card>
  );
};

ContentCard.propTypes = {
  content: PropTypes.shape({
    content: PropTypes.string,
    resource: PropTypes.string,
    attributeType: PropTypes.string,
    title: PropTypes.string,
    alignment: PropTypes.string,
  }).isRequired,
};

export default ContentCard;
